/**
 * i18n
 * 进行初始化相关操作
 * 进行语言包相关缓存
 * 当前语言相关处理
 *
 * Created by Samuel on 2022/8/16
 */
import * as _ from 'lodash-es';

import { lang } from '@core/i18n/lang';
import { createLanguageBlock } from '@core/i18n/Provider';
import { parseUrl } from '@core/utils';
import { jsonp } from '@core/utils/jsonp';

import request from '@/libs/request';
export { lt, tokenHandler } from '@core/i18n/tokenHandler';

const _LANG_MAPPING = {
	'zh-hans': 'cn',
	'zh-hant': 'tw',
	zh: 'cn',
	cn: 'cn',
	tw: 'tw',
	en: 'en',
	ja: 'ja',
	fr: 'fr',
	de: 'de',
	ko: 'ko',
	ru: 'ru',
	es: 'es',
	ar: 'ar',
	it: 'it',
};
const normalizedLangTag = (tag) => {
	let lang = null;
	if (tag) {
		lang = _LANG_MAPPING[tag.toLowerCase()];
		if (!lang && tag.includes('-')) {
			let tags = tag.split('-');
			do {
				tags.pop();
				lang = _LANG_MAPPING[tags.join('-').toLowerCase()];
			} while (!lang && tags.length > 1);
		}
	}

	return lang || 'en';
};

// 缓存 MgIntl 实例到全局状态下
const _MGINTL_DATA_CACHE = {};
// 初始化语言相关管理缓存
class MgIntl {
	constructor(config) {
		this.language = normalizedLangTag(config.language);
		this.packagePath = config.path;
		this.namespace = config.namespace;
		// 语言包文件名相关的模板，默认情况下会根据 namespace-language.json?v=version 的格式进行加载，有模板时，会进行字符串替换确定文件名
		this.name = config.name;
		this.languageInfo = null;
		this.status = null;
		this.version = config.version;
		this._support = config.supportedLanguage;
		if (this._support) {
			this.language = this._support(this.language);
		}
		// get data in cache
		const _cacheNamespace = this.namespace || 'default';
		if (
			_MGINTL_DATA_CACHE[_cacheNamespace] &&
			_MGINTL_DATA_CACHE[_cacheNamespace].lang === this.language &&
			_MGINTL_DATA_CACHE[_cacheNamespace].info != null
		) {
			this.status = 'succeed';
			this.languageInfo = _MGINTL_DATA_CACHE[_cacheNamespace].info;
		}
	}

	// 支持的模板变量
	// [fullName] - language
	// [ext] - json / js
	// [version] - version
	requestPath({ name, namespace, language, jsonp = false }) {
		name = name || '[fullName].[ext]?v=[version]';

		let filename = language;
		let version = '';
		if (namespace) {
			filename = `${namespace}-${language}`;
		}
		if (this.version && _.isPlainObject(this.version)) {
			if (this.version[filename]) {
				version = this.version[filename];
			}
		} else if (this.version) {
			version = this.version;
		}

		const fileNamePath = name
			.replace('[fullName]', filename)
			.replace('[lang]', language)
			.replace('[ext]', jsonp ? 'js' : 'json')
			.replace('[version]', version || '');

		// 增加对于jsonp的相关支持
		return `${this.packagePath}/${fileNamePath}`;
	}

	changeLanguage(lang, callback) {
		this.language = normalizedLangTag(lang);
		if (this._support) {
			this.language = this._support(this.language);
		}
		this.loadLanguagePack().then(() => {
			callback?.();
		});
	}

	// 加载语言包
	async loadLanguagePack() {
		if (this.status === 'pending') {
			return;
		}
		this.status = 'pending';
		try {
			let res;
			let _pathConf = {
				namespace: this.namespace,
				language: this.language,
				name: this.name,
			};
			if (parseUrl(this.packagePath).host !== parseUrl(location.href).host) {
				let filename = this.language;
				if (this.namespace) {
					filename = `${this.namespace}-${this.language}`;
				}
				// NOTICE!! 最终文件一定需要callback-name, 作为js端设置的回调函数应答
				res = (await jsonp(this.requestPath({ ..._pathConf, jsonp: true }), {}, filename)) || {};
			} else {
				res = await request(this.requestPath(_pathConf), {
					method: 'GET',
					disabledErrorList: true,
				});
			}

			this.status = 'succeed';
			this.languageInfo = res;
			// 缓存当前的语言包信息
			updateLangCache(this.namespace, this.language, this.languageInfo);
		} catch (e) {
			console.log('failed!', e);
			this.status = 'failed';
			throw e;
		}
	}
}

// 手动从外部设置语言包缓存信息（通常用于在渲染发生之前） 暂时没有使用，当后端有返回语言包信息时，可以使用
export const updateLangCache = (namespace, language, info) => {
	_MGINTL_DATA_CACHE[namespace || 'default'] = {
		lang: language,
		info: info,
		l: lang(info),
	};
};

// 从全局缓存中读取语言包信息，在react体系外使用
export const getLanguage = (key, params = null, namespace = 'default') => {
	if (_MGINTL_DATA_CACHE[namespace]) {
		return _MGINTL_DATA_CACHE[namespace].l(key, params);
	}
	if (_MGINTL_DATA_CACHE && _MGINTL_DATA_CACHE[namespace]) {
		console.log(`cannot find ${key} in ${namespace}`);
	}
	return null;
};

const I18n = {
	MgIntl,
	normalizedLangTag,
	lang,
	createLanguageBlock,
};

export default I18n;